import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { startCase } from "lodash";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import { Badge } from "@/app/admin/_common/ui/badge";
import { Skeleton } from "@/app/admin/_common/ui/skeleton";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import { cn } from "@/app/_utils/cn";
import { dayjsUtcToTz, dayjsTz } from "@/app/_utils/dayjs";
import { getShowTypeColor } from "@/app/_utils/constants";
import { getSignupsTimestamps, isNewSignupEvent } from "@/app/_utils/guestSignups";
import { Users, BellOff, Star, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import type { SortModel } from "../../waitlistPageModel";
import WaitlistRowMenu from "./WaitlistRowMenu";

const formatter = new Intl.NumberFormat("en-US");
const LOAD_MORE_BUFFER = 200;

const SIGNUP_BADGE_CLASSES = {
  new: "border-emerald-200 bg-emerald-50 text-emerald-700",
  repeat: "border-orange-200 bg-orange-50 text-orange-700",
};
const SIGNUP_DOT_CLASSES = {
  new: "bg-emerald-500",
  repeat: "bg-orange-500",
};

// The row-action column pins to the right edge during horizontal scroll. `bg-inherit` makes
// it pick up the row's own background (hover, or a row.color) instead of a fixed fill, so it
// can't diverge from the row it belongs to. No left border — it reads as part of the row.
const STICKY_ACTION_CELL = "sticky right-0 bg-inherit";

function renderIntake(intake: number, intake_method: string) {
  if (intake === 16) return "Stripe";
  const waitlist = [1, 2, 3, 8, 4, 5, 6, 7, 9, 13, 14];
  if (waitlist.includes(intake)) return "Waitlist";
  return intake_method;
}

function TypeDots({ comedy, magic }: { comedy: boolean; magic: boolean }) {
  if (!comedy && !magic) return <span className="text-muted-foreground">-</span>;
  return (
    <div className="flex items-center gap-1">
      {comedy && (
        <span
          className="flex size-5 flex-none items-center justify-center rounded-full text-[11px] font-semibold text-white"
          style={{ backgroundColor: getShowTypeColor("comedy") }}
        >
          C
        </span>
      )}
      {magic && (
        <span
          className="flex size-5 flex-none items-center justify-center rounded-full text-[11px] font-semibold text-white"
          style={{ backgroundColor: getShowTypeColor("magic") }}
        >
          M
        </span>
      )}
    </div>
  );
}

function SortableHead({
  field,
  label,
  className,
  sortModel,
  onSort,
}: {
  field: string;
  label: string;
  className?: string;
  sortModel?: SortModel;
  onSort: (next: SortModel) => void;
}) {
  const current = sortModel?.[0]?.field === field ? sortModel[0].sort : null;
  const Icon = current === "asc" ? ArrowUp : current === "desc" ? ArrowDown : ArrowUpDown;
  return (
    <TableHead
      className={`h-8 cursor-pointer select-none text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground ${className ?? ""}`}
      onClick={() => onSort([{ field, sort: current === "asc" ? "desc" : "asc" }])}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`size-3 ${current ? "opacity-100" : "opacity-30"}`} />
      </span>
    </TableHead>
  );
}

export default function WaitlistTable({
  rows,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  sortModel,
  onSort,
  onEdit,
  onDelete,
}: {
  rows: any[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  sortModel?: SortModel;
  onSort: (next: SortModel) => void;
  onEdit: (guest: any) => void;
  onDelete: (guest: any) => void;
}) {
  const navigate = useNavigate();
  const columnCount = 12;
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 41,
    overscan: 10,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
      : 0;

  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;
    if (lastItem.index >= rows.length - LOAD_MORE_BUFFER && hasMore && !isLoadingMore) {
      onLoadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualItems, rows.length, hasMore, isLoadingMore]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerRef={parentRef}
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[1400px] table-fixed", !isLoading && rows.length === 0 && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-20 hover:bg-muted [&>th]:bg-muted">
            <SortableHead field="index" label="Pos" className="w-[6%] text-center" sortModel={sortModel} onSort={onSort} />
            <SortableHead field="first_name" label="Name" className="w-[15%]" sortModel={sortModel} onSort={onSort} />
            <TableHead className="h-8 w-[6%] text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Shows
            </TableHead>
            <TableHead className="h-8 w-[7%] text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Signups
            </TableHead>
            <TableHead className="h-8 w-[9%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Instagram
            </TableHead>
            <TableHead className="h-8 w-[7%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Types
            </TableHead>
            <TableHead className="h-8 w-[8%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Interest
            </TableHead>
            <TableHead className="h-8 w-[12%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Email
            </TableHead>
            <TableHead className="h-8 w-[6%] text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Intake
            </TableHead>
            <TableHead className="h-8 w-[13%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Intake Other
            </TableHead>
            <SortableHead field="signup_date" label="Signup Date" className="w-[11%]" sortModel={sortModel} onSort={onSort} />
            {/* z-30 so the header/action corner stays above both sticky axes. */}
            <TableHead className={cn("h-8 w-12 pr-6 text-right z-30", STICKY_ACTION_CELL)} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 30 }).map((_, i) => (
              <TableRow key={i} className="bg-card hover:bg-card">
                <TableCell className="py-2"><Skeleton className="mx-auto h-3 w-8" /></TableCell>
                <TableCell className="py-2"><Skeleton className="h-3 w-28" /></TableCell>
                <TableCell className="py-2"><Skeleton className="mx-auto h-3 w-4" /></TableCell>
                <TableCell className="py-2"><Skeleton className="mx-auto h-3 w-4" /></TableCell>
                <TableCell className="py-2"><Skeleton className="h-3 w-16" /></TableCell>
                <TableCell className="py-2"><Skeleton className="mx-auto size-5 rounded-full" /></TableCell>
                <TableCell className="py-2"><Skeleton className="mx-auto h-3 w-4" /></TableCell>
                <TableCell className="py-2"><Skeleton className="h-3 w-32" /></TableCell>
                <TableCell className="py-2"><Skeleton className="mx-auto h-3 w-12" /></TableCell>
                <TableCell className="py-2"><Skeleton className="h-3 w-16" /></TableCell>
                <TableCell className="py-2"><Skeleton className="h-3 w-24" /></TableCell>
                <TableCell className={cn("py-2 pr-6", STICKY_ACTION_CELL)}>
                  <Skeleton className="ml-auto size-4 rounded" />
                </TableCell>
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={columnCount} className="h-full p-0">
                <CuoEmpty icon={Users} text="No guests on the waitlist" />
              </TableCell>
            </TableRow>
          ) : (
            <>
              {paddingTop > 0 && (
                <tr>
                  <td colSpan={columnCount} style={{ height: paddingTop }} />
                </tr>
              )}
              {virtualItems.map((virtualRow) => {
              const row = rows[virtualRow.index];
              const isNew = isNewSignupEvent(row.signups);
              const signupAt = dayjsUtcToTz(row.signup_date);
              const showBadge =
                signupAt.isAfter(dayjsTz("2024-12-18")) &&
                !signupAt.isBefore(dayjsTz().subtract(30, "day"));

              return (
                <TableRow
                  key={row.id}
                  // Opaque base + opaque hover: the sticky cell mirrors this via bg-inherit,
                  // and a translucent hover would paint twice and read darker than the row.
                  className="cursor-pointer bg-card hover:bg-muted"
                  style={row.color ? { backgroundColor: row.color } : undefined}
                  onClick={() => onEdit(row)}
                >
                  <TableCell className="py-2 text-center text-[12.5px] text-muted-foreground">
                    {formatter.format(row.index)}
                  </TableCell>
                  <TableCell className="py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="truncate text-[13px] text-foreground hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/guests/${row.id}`);
                        }}
                      >
                        {row.first_name} {row.last_name}
                      </span>
                      {showBadge && (
                        <Badge
                          variant="outline"
                          className={`gap-1 rounded-[7px] px-1.5 py-0 text-[10px] font-normal ${
                            SIGNUP_BADGE_CLASSES[isNew ? "new" : "repeat"]
                          }`}
                        >
                          <span
                            className={`size-1.5 flex-none rounded-full ${
                              SIGNUP_DOT_CLASSES[isNew ? "new" : "repeat"]
                            }`}
                          />
                          {isNew ? "New" : "Repeat"}
                        </Badge>
                      )}
                      {!row.subscribed && <BellOff className="size-3.5 flex-none text-muted-foreground" />}
                      {row.num_shows >= 2 && <Star className="size-3.5 flex-none fill-amber-400 text-amber-400" />}
                    </span>
                  </TableCell>
                  <TableCell className="py-2 text-center text-[12.5px] text-muted-foreground">
                    {row.num_shows > 0 ? row.num_shows : "-"}
                  </TableCell>
                  <TableCell className="py-2 text-center text-[12.5px] text-muted-foreground">
                    {getSignupsTimestamps(row.signups).length || "-"}
                  </TableCell>
                  <TableCell className="truncate py-2 text-[12.5px] text-muted-foreground">
                    {row.instagram || "-"}
                  </TableCell>
                  <TableCell className="py-2">
                    <TypeDots comedy={row.attendedComedyShow} magic={row.attendedMagicShow} />
                  </TableCell>
                  <TableCell className="py-2">
                    <TypeDots comedy={row.interested_in_comedy} magic={row.interested_in_magic} />
                  </TableCell>
                  <TableCell className="truncate py-2 text-[12.5px] text-muted-foreground">
                    {row.email || "-"}
                  </TableCell>
                  <TableCell className="py-2 text-center text-[12.5px] text-muted-foreground">
                    {renderIntake(row.intake, row.intake_method)}
                  </TableCell>
                  <TableCell className="truncate py-2 text-[12.5px] text-muted-foreground">
                    {startCase(row.other)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap py-2 text-[12.5px] text-muted-foreground">
                    {dayjsUtcToTz(row.signup_date).format("MMM D, YYYY h:mm a")}
                  </TableCell>
                  <TableCell
                    className={cn("py-2 pr-6 text-right", STICKY_ACTION_CELL)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <WaitlistRowMenu guest={row} onEdit={() => onEdit(row)} onDelete={() => onDelete(row)} />
                  </TableCell>
                </TableRow>
              );
              })}
              {paddingBottom > 0 && (
                <tr>
                  <td colSpan={columnCount} style={{ height: paddingBottom }} />
                </tr>
              )}
              {isLoadingMore && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={columnCount} className="py-2 text-center text-[12px] text-muted-foreground">
                    Loading more…
                  </TableCell>
                </TableRow>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
